import { createContext, useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useSubscriptionGate, getOwnerSubscriptionState, type SubscriptionState } from "@/hooks/useSubscriptionGate";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Context exposed to flow pages so they can check the owner's subscription. */
const FlowOwnerContext = createContext<{ isPaid: boolean }>({ isPaid: false });

/** Returns true when the flow owner has an active paid subscription (not trial/grace). */
export function useFlowOwner() {
  return useContext(FlowOwnerContext);
}

/**
 * Wraps public flow pages to check the flow owner's subscription state.
 *
 * - If the logged-in user IS the owner, we use the client-side Autumn check
 *   (useSubscriptionGate) which always has accurate data.
 * - For everyone else (visitors, other users), we fall back to the server-side
 *   RPC which checks the profiles table.
 *
 * States:
 * - Active / None: renders children (treat "none" as active because if a flow
 *   exists the owner had access when they created it, and "none" usually means
 *   we just can't detect the Autumn subscription server-side)
 * - Grace: renders children + owner-only banner
 * - Expired: shows a deactivation message
 */
export function FlowAccessGate({ children }: { children: React.ReactNode }) {
  const { flowId } = useParams();
  const { user, isLoading: authLoading } = useAuth();
  const { subscriptionState: clientSubState, graceDaysRemaining: clientGraceDays, isAccessLoaded } = useSubscriptionGate();
  const [ownerState, setOwnerState] = useState<SubscriptionState | null>(null);
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  const [graceDaysRemaining, setGraceDaysRemaining] = useState(0);
  const [ownerIsPaid, setOwnerIsPaid] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkOwner = async () => {
      if (!flowId) {
        setOwnerState("expired");
        setLoading(false);
        return;
      }

      // Wait for auth to settle. If we run while authLoading is true, `user`
      // is null and we'd misclassify a logged-in owner as a visitor — flashing
      // the gate before the next effect run corrects it. The effect re-fires
      // when authLoading flips false.
      if (authLoading) return;

      let flowUserId: string;
      try {
        const { data: flow, error } = await supabase
          .from("flows")
          .select("user_id")
          .eq("id", flowId)
          .single();

        if (error || !flow?.user_id) {
          setOwnerState("expired");
          setLoading(false);
          return;
        }
        flowUserId = flow.user_id;
        setOwnerUserId(flowUserId);
      } catch {
        setOwnerState("expired");
        setLoading(false);
        return;
      }

      // Owner branch: use the client-side Autumn check (always accurate for
      // the signed-in owner). Hold the spinner until BOTH Autumn customer
      // data and the legacy lifetime-access profile read have resolved —
      // otherwise clientSubState defaults to "none" and we'd briefly flash a
      // gate before the next effect run corrects it. The effect re-fires
      // when isAccessLoaded flips true.
      if (user?.id === flowUserId) {
        if (!isAccessLoaded) return;
        setOwnerState(clientSubState);
        setGraceDaysRemaining(clientGraceDays);
        const { data: ownProfile } = await supabase
          .from("profiles")
          .select("has_lifetime_access, subscription_active_until")
          .eq("id", flowUserId)
          .single();
        if (ownProfile) {
          setOwnerIsPaid(
            ownProfile.has_lifetime_access === true ||
            (!!ownProfile.subscription_active_until && new Date(ownProfile.subscription_active_until) > new Date())
          );
        }
        setLoading(false);
        return;
      }

      // Visitor / other-user branch: use the server-side RPC.
      try {
        const { data: rpcResult } = await supabase.rpc("get_owner_subscription_info", {
          p_user_id: flowUserId,
        });
        if (rpcResult) {
          setOwnerState((rpcResult.state as SubscriptionState) || "expired");
          setGraceDaysRemaining(rpcResult.grace_days_remaining ?? 0);
          setOwnerIsPaid(rpcResult.is_paid === true);
        } else {
          const info = await getOwnerSubscriptionState(flowUserId, supabase);
          setOwnerState(info.state);
          setGraceDaysRemaining(info.graceDaysRemaining);
        }
      } catch {
        setOwnerState("expired");
      }
      setLoading(false);
    };

    checkOwner();
  }, [flowId, user?.id, authLoading, clientSubState, clientGraceDays, isAccessLoaded]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Active: render flow. "none" used to be permissive (legacy data), but under
  // the current architecture free users can save flows without subscribing —
  // their state is "none", and their flows are explicitly NOT published.
  const ctxValue = { isPaid: ownerIsPaid };
  const isOwner = user?.id === ownerUserId;

  if (ownerState === "active") {
    return <FlowOwnerContext.Provider value={ctxValue}>{children}</FlowOwnerContext.Provider>;
  }

  // None: owner is on the free tier (never trialed or subscribed). The flow is
  // a draft — the owner can preview/edit it freely; visitors get a not-found
  // screen so we don't expose unpublished URLs.
  if (ownerState === "none") {
    if (isOwner) {
      return <FlowOwnerContext.Provider value={ctxValue}>{children}</FlowOwnerContext.Provider>;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            This page is no longer available
          </h2>
          <p className="text-gray-500 text-sm">
            The owner of this flow has an inactive subscription. Please contact them for more information.
          </p>
        </div>
      </div>
    );
  }

  // Grace: flow is live, but show banner to owner
  if (ownerState === "grace") {
    return (
      <FlowOwnerContext.Provider value={ctxValue}>
        {isOwner && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
            <div className="max-w-3xl mx-auto flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <span className="font-medium">This flow is part of an expired trial.</span>{" "}
                  It will be deactivated in{" "}
                  <span className="font-semibold">{graceDaysRemaining} day{graceDaysRemaining !== 1 ? "s" : ""}</span>.
                </p>
              </div>
              <Link to="/subscription">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 px-3">
                  Upgrade to keep it live
                </Button>
              </Link>
            </div>
          </div>
        )}
        {children}
      </FlowOwnerContext.Provider>
    );
  }

  // Expired: flow is deactivated
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          This page is no longer available
        </h2>
        <p className="text-gray-500 text-sm">
          The owner of this flow has an inactive subscription. Please contact them for more information.
        </p>
      </div>
    </div>
  );
}
